import { Language } from '../../presentation/i18n/translations';
import { DeckType } from '../entities/Room';
import { TSHIRT_ORDER, isTShirtSize } from './TShirtDeck';

interface MessageBank {
  consensus: string[];
  close: string[];
  far: string[];
  coffee: string[];
  question: string[];
  unusual: string;
}

const PT: MessageBank = {
  consensus: [
    "🎯 Uau! Todo mundo concordou! Isso é raro como unicórnio!",
    "🏆 Consenso perfeito! Vocês leram a mente uns dos outros!",
    "✨ Alinhamento total! O time está em harmonia!",
    "🎉 Todos iguais! Será que alguém copiou?",
    "🌟 Perfeito! O time está sincronizado!",
  ],
  close: [
    "👏 Quase consenso! Pequenas diferenças, grande alinhamento!",
    "🤝 Estimativas bem próximas! Ótimo trabalho em equipe!",
    "📊 Divergência leve! Uma breve discussão resolve!",
  ],
  far: [
    "🌊 Grande divergência! Tempo de alinhar expectativas!",
    "🎭 Opiniões bem diferentes! Vamos conversar?",
    "🔥 Estimativas distantes! Isso vai ser uma discussão interessante!",
    "🤔 Algo não está claro para todos! Bora esclarecer?",
    "🎪 Circo das estimativas! Cada um viu uma história diferente?",
  ],
  coffee: [
    "☕ Alguém pediu café! Pausa técnica solicitada!",
    "☕ Café time! A história precisa de mais clareza!",
    "☕ Houston, temos um problema... e ele quer café!",
  ],
  question: [
    "❓ Muitas dúvidas! Será que entendemos a história?",
    "🤷 Interrogações em série! Hora de refinar!",
  ],
  unusual: "🤔 Resultados incomuns!",
};

const EN: MessageBank = {
  consensus: [
    "🎯 Wow! Everyone agreed! That's as rare as a unicorn!",
    "🏆 Perfect consensus! You read each other's minds!",
    "✨ Full alignment! The team is in harmony!",
    "🎉 All the same! Did somebody peek?",
    "🌟 Perfect! The team is in sync!",
  ],
  close: [
    "👏 Almost there! Tiny differences, big alignment!",
    "🤝 Estimates are very close! Great teamwork!",
    "📊 Minor divergence! A quick chat will sort it out!",
  ],
  far: [
    "🌊 Huge divergence! Time to align expectations!",
    "🎭 Very different opinions! Shall we talk?",
    "🔥 Estimates are far apart! This is going to be an interesting discussion!",
    "🤔 Something isn't clear to everyone! Let's clarify?",
    "🎪 Estimation circus! Did everyone read a different story?",
  ],
  coffee: [
    "☕ Someone ordered coffee! Tech break requested!",
    "☕ Coffee time! The story needs more clarity!",
    "☕ Houston, we have a problem... and it wants coffee!",
  ],
  question: [
    "❓ Lots of doubts! Did we really understand the story?",
    "🤷 Question marks everywhere! Time to refine!",
  ],
  unusual: "🤔 Unusual results!",
};

const BANKS: Record<Language, MessageBank> = { pt: PT, en: EN };

function getRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

export function getVotingMessage(
  votes: string[],
  language: Language = 'en',
  deckType: DeckType = 'fibonacci'
): string {
  const bank = BANKS[language] ?? EN;

  const hasCoffee = votes.some(v => v === '☕');
  const hasQuestion = votes.some(v => v === '?');

  if (hasCoffee) return getRandom(bank.coffee);
  if (hasQuestion) return getRandom(bank.question);

  const ordinals = deckType === 'tshirt'
    ? votes.filter(isTShirtSize).map(v => TSHIRT_ORDER.indexOf(v))
    : votes.filter(v => !isNaN(Number(v))).map(Number);

  if (ordinals.length === 0) return bank.unusual;

  const max = Math.max(...ordinals);
  const min = Math.min(...ordinals);
  const spread = max - min;

  if (spread === 0) return getRandom(bank.consensus);
  const closeThreshold = deckType === 'tshirt' ? 2 : 3;
  if (spread <= closeThreshold) return getRandom(bank.close);
  return getRandom(bank.far);
}
